import { z } from 'zod';
import { ZBaseIntegrationConfig } from './base.js';
export const TIKTOK_INTEGRATION_NAME = 'tiktok';
/**
 * Optional string that treats empty / whitespace-only input as absent
 * (`undefined`) before applying the min-length check, so a field cleared in the
 * form (or kept hidden behind a disabled server-side toggle) does not trip the
 * length requirement.
 *
 * Unlike `z.preprocess`, this keeps the schema's input and output types aligned
 * (`string | undefined`). `z.preprocess` widens the *input* type to `unknown`,
 * which makes `@hookform/resolvers` infer the field as required-`unknown` and
 * breaks the `useForm<ShopIntegrationConfig>` resolver type for the whole
 * discriminated union.
 */
function optionalTrimmedMin(min, message) {
    return z
        .string()
        .trim()
        .transform((value) => (value === '' ? undefined : value))
        .pipe(z.string().min(min, message).optional())
        .optional();
}
export const ZTiktokCredentials = z.object({
    /** Public web-event-set id (TikTok `event_source_id`). Non-empty when present. */
    pixelCode: optionalTrimmedMin(1, 'Pixel code cannot be empty'),
    /**
     * Secret Events API token (TikTok `Access-Token` header). No documented
     * format — a heuristic min length catches truncated copy-pastes.
     */
    accessToken: optionalTrimmedMin(20, 'Access token looks too short'),
});
export const ZTiktokSettings = z.object({
    /** Default true (set in the form) — fire the Purchase pixel via `window.ttq`. */
    enableClientSide: z.boolean().optional(),
    /** Default false — server-side Events API is opt-in once credentials are saved. */
    enableServerSide: z.boolean().optional(),
});
export const ZTiktokIntegrationConfig = ZBaseIntegrationConfig.extend(z.object({
    integrationName: z.literal(TIKTOK_INTEGRATION_NAME),
    category: z.literal('analytics'),
    // `.optional()` (NOT `.default({})`): keeps the schema's INPUT and OUTPUT
    // types aligned (`object | undefined`). `.default({})` makes the input
    // optional but the output required, and that input/output divergence breaks
    // the `useForm<ShopIntegrationConfig>` discriminated-union resolver type
    // (react-hook-form sees `Resolver<input>` vs `Resolver<output>` as
    // unrelated). Optional is also backward-compatible with the bare seeded
    // configs the CLI wrote to every shop (a missing object parses as
    // `undefined`, and the `.check()` below short-circuits via `?.`). Mirrors
    // the Snapchat variant, which builds with this same resolver.
    credentials: ZTiktokCredentials.optional(),
    settings: ZTiktokSettings.optional(),
}).shape).check((ctx) => {
    // OPT-IN: only enforce credentials when server-side tracking is EXPLICITLY
    // enabled (`=== true`). Bare/seeded configs (no settings) and client-side-only
    // merchants can save without credentials.
    const config = ctx.value;
    if (config.settings?.enableServerSide !== true)
        return;
    if (!config.credentials?.pixelCode) {
        ctx.issues.push({
            code: 'custom',
            input: ctx.value,
            path: ['credentials', 'pixelCode'],
            message: 'Required to enable server-side tracking',
        });
    }
    if (!config.credentials?.accessToken) {
        ctx.issues.push({
            code: 'custom',
            input: ctx.value,
            path: ['credentials', 'accessToken'],
            message: 'Required to enable server-side tracking',
        });
    }
});
//# sourceMappingURL=tiktok.js.map